import React, { useCallback, useEffect, useMemo, useState } from "react";
import { PageName, Stack } from "../components/StyledComponents";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { useMSPData } from "../hooks/useMSPData";
import { useMSPCompanyData } from "../hooks/useMSPCompanyData";
import { MSPCompanyInformation } from "../features/common/MSPCompanyInformation";
import { ContactsSection } from "../features/common/ContactsSection";
import { SubscriptionsSection } from "../features/common/SubscriptionsSection";
import AddMspProductForm from "../features/msp/AddMspProductForm";
import AddMspProductLicenseForm from "../features/msp/AddMspProductLicenseForm";
import { useNavigate, useParams } from "react-router-dom";
import { setSelectedMspId } from "../features/msp/slices/selectedMspSlice";
import EditMSPInformationForm from "../features/msp/EditMSPInformationForm";
import ContactForm from "../features/common/ContactForm";
import { Subscription, UserContact } from "../features/common/types";
import { SETI_PRODUCTS } from "../constants/products";
import ReduceMspProductLicenseForm from "../features/msp/ReduceMspProductLicenseForm";
import { Modal } from "../components/Modal";
import ConfirmDialog from "../components/ConfirmDialog";
import { cancelMspSubscription, deleteMSPContact } from "../features/msp/services/mspService";

const MSPInformation: React.FC = () => {
  const dispatch = useAppDispatch();
  // read param from URL (string | undefined)
  const params = useParams<{ mspId?: string }>();
  const paramMspId = params.mspId ? Number(params.mspId) : undefined;

  // read selected from redux
  const selectedMspId = useAppSelector(
    (s: any) => s.selectedMsp?.selectedMspId
  );

  // prefer URL param, then redux, otherwise 0/undefined
  const mspIdToUse = useMemo(() => {
    if (typeof paramMspId === "number" && !Number.isNaN(paramMspId))
      return paramMspId;
    if (typeof selectedMspId === "number" && !Number.isNaN(selectedMspId))
      return selectedMspId;
    return undefined;
  }, [paramMspId, selectedMspId]);

  // sync: if URL has id, ensure redux holds same id so other components keep working
  useEffect(() => {
    if (typeof paramMspId === "number" && !Number.isNaN(paramMspId)) {
      // dispatch only if different
      if (selectedMspId !== paramMspId) {
        dispatch(setSelectedMspId(paramMspId));
      }
    }
  }, [paramMspId, selectedMspId, dispatch]);

  const { msp, fetchData } = useMSPData(Number(mspIdToUse ?? 0));

  const { companyAddress, contacts, subscriptions } = useMSPCompanyData(msp);
  // --- inside MSPInformation component, after:
  // const { companyAddress, contacts, subscriptions } = useMSPCompanyData(msp);

  const existingProductNames = useMemo(() => {
    if (!subscriptions) return new Set<string>();
    return new Set(
      subscriptions
        .filter((s: Subscription) => s?.subscription_status === "active")
        .map((s: Subscription) => s?.planPrice?.plan?.product?.product_name)
        .filter(Boolean)
        .map((n: string) => n.toUpperCase().trim())
    );
  }, [subscriptions]);

  // Check which ones exist
  const hasDesktop = existingProductNames.has("SETI-DESKTOP");
  const hasBrowser = existingProductNames.has("SETI-BROWSER");

  // If both present -> no add-product allowed.
  // If one present -> allow adding only the other one (so allowedProducts holds the missing product).
  const allowedProducts = useMemo(() => {
    if (hasDesktop && hasBrowser) return []; // none allowed
    return SETI_PRODUCTS.filter((p) => !existingProductNames.has(p));
  }, [hasDesktop, hasBrowser, existingProductNames]);

  const canAddProduct = allowedProducts.length > 0;

  const [selectedSubscription, setSelectedSubscription] = useState<Subscription>();

  const [showEditContactForm, setShowEditContactForm] = useState(false);
  const [showAddContactForm, setShowAddContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<UserContact>();
  const [deletingContact, setDeletingContact] = useState<UserContact>();
  const [showDeleteContact, setShowDeleteContact] = useState(false);

  const [showAddMSPProductForm, setShowAddMSPProductForm] = useState(false);
  const [showAddMSPProductLicenseForm, setShowAddMSPProductLicenseForm] =
    useState(false);
  const [showReduceMSPProductLicenseForm, setShowReduceMSPProductLicenseForm] =
    useState(false);
  const [showCancelMSPSubscription, setShowCancelMSPSubscription] =
    useState(false);
  const [showEditMspForm, setShowEditMspForm] = useState(false);

  const handleEditCompanyInfo = useCallback(() => setShowEditMspForm(true), []);
  const handleEditSuccess = useCallback(() => {
    setShowEditMspForm(false);
    fetchData();
  }, [fetchData]);

  const handleEditCancel = useCallback(() => {
    setShowEditMspForm(false);
  }, []);
  const handleAddContact = useCallback(() => {
    console.log("add contact");
    setShowAddContactForm(true);
  }
    , []);
  const handleEditContact = useCallback(
    (c: UserContact) => {
      console.log("edit contact", c);
      setEditingContact(c);
      setShowEditContactForm(true);
    },
    []
  );
  const handleDeleteContact = useCallback(
    (c: UserContact) => {
      console.log("delete contact", c)
      setDeletingContact(c);
      setShowDeleteContact(true);
    },
    []
  );

  const handleAddProduct = useCallback(
    () => setShowAddMSPProductForm(true),
    []
  );
  const handleAddProductLicense = useCallback((subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowAddMSPProductLicenseForm(true);
  }, []);

  const handleReduceProductLicense = useCallback((subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowReduceMSPProductLicenseForm(true);
  }, []);

  const handleCancelSubscription = useCallback((subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowCancelMSPSubscription(true);
  }, []);

  if (showAddContactForm) {
    return <ContactForm entity_type="msp" action="add" mspId={mspIdToUse!}
      onCancel={() => setShowAddContactForm(false)}
      onSuccess={() => {
        setShowAddContactForm(false);
        fetchData()
      }} />
  }
  if (showEditContactForm) {
    return <ContactForm entity_type="msp" action="edit" mspId={mspIdToUse!} mspContactId={Number(editingContact?.contact_id)}
      onCancel={() => setShowEditContactForm(false)}
      onSuccess={() => {
        setShowEditContactForm(false);
        fetchData()
      }} />
  }

  if (showEditMspForm) {
    return (
      <EditMSPInformationForm
        msp={msp}
        onSuccess={handleEditSuccess}
        onCancel={handleEditCancel}
      />
    );
  }

  return (
    <div style={{ padding: 20 }}>
      <PageName>View MSP: {msp?.msp_name ?? "-"}</PageName>
      <Stack>
        <MSPCompanyInformation
          msp={msp}
          companyAddress={companyAddress}
          onEdit={handleEditCompanyInfo}
        />

        <ContactsSection
          contacts={contacts}
          onAddContact={handleAddContact}
          onEditContact={handleEditContact}
          onDeleteContact={handleDeleteContact}
        />

        <SubscriptionsSection
          subscriptions={subscriptions}
          onAddProduct={canAddProduct ? handleAddProduct : undefined}
          onAddProductLicense={handleAddProductLicense}
          onReduceProductLicense={handleReduceProductLicense}
          onCancelSubscription={handleCancelSubscription}
        />
      </Stack>

      {/* Modal: Add MSP Product */}
      {showAddMSPProductForm && (
        <Modal
          onClose={() => setShowAddMSPProductForm(false)}
          ariaLabel="Add MSP Product"
        >
          <AddMspProductForm
            allowedProducts={allowedProducts}
            onProductAdded={() => {
              setShowAddMSPProductForm(false);
              fetchData();
            }}
            onCancel={() => setShowAddMSPProductForm(false)}
          />
        </Modal>
      )}

      {/* Modal: Add MSP Product License */}
      {showAddMSPProductLicenseForm && selectedSubscription && (
        <Modal
          onClose={() => {
            setShowAddMSPProductLicenseForm(false);
            setSelectedSubscription(undefined);
          }}
          ariaLabel="Add MSP Product License"
        >
          <AddMspProductLicenseForm
            subscription={selectedSubscription}
            onProductLicenseAdded={() => {
              setShowAddMSPProductLicenseForm(false);
              setSelectedSubscription(undefined);
              fetchData();
            }}
            onCancel={() => {
              setShowAddMSPProductLicenseForm(false);
              setSelectedSubscription(undefined);
            }}
          />
        </Modal>
      )}

      {/* Modal: Reduce MSP Product License */}
      {showReduceMSPProductLicenseForm && selectedSubscription && (
        <Modal
          onClose={() => {
            setShowReduceMSPProductLicenseForm(false);
            setSelectedSubscription(undefined);
          }}
          ariaLabel="Reduce MSP Product License"
        >
          <ReduceMspProductLicenseForm
            subscription={selectedSubscription}
            onProductLicenseReduced={() => {
              setShowReduceMSPProductLicenseForm(false);
              setSelectedSubscription(undefined);
              fetchData();
            }}
            onCancel={() => {
              setShowReduceMSPProductLicenseForm(false);
              setSelectedSubscription(undefined);
            }}
          />
        </Modal>
      )}

      {showCancelMSPSubscription && selectedSubscription && (
        <ConfirmDialog
          open={showCancelMSPSubscription}
          message="Cancel this subscription?"
          onConfirm={async () => {
            setShowCancelMSPSubscription(false);
            await cancelMspSubscription(selectedMspId, selectedSubscription.id);
            console.log("fetch data after cancel");
            await fetchData();
          }}
          onCancel={() => {
            setShowCancelMSPSubscription(false);
          }
          }
        />
      )}

      {showDeleteContact && deletingContact && (
        <ConfirmDialog
          open={showDeleteContact}
          message="Delete this contact?"
          onConfirm={async () => {
            setShowDeleteContact(false);
            await deleteMSPContact(mspIdToUse!, Number(deletingContact.contact_id));
            console.log("fetch data after cancel");
            await fetchData();
          }}
          onCancel={() => {
            setShowDeleteContact(false);
          }
          }
        />
      )}
    </div>
  );
};

export default MSPInformation;
